/* eslint-disable react/no-children-prop */
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, DrawerHeader, Loading, Text } from 'components/styleguide';
import { useRootTranslation } from 'utils/hooks';
import { Divider, Grid } from '@material-ui/core';
import { Trans } from 'react-i18next';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  getCommercialTemplateFile,
  validateCommercialImportFile,
  startCommercialImportCall,
} from 'redux-core/import-commercial-tracks/services';
import { uploadCommercialImportFile } from 'redux-core/import-commercial-tracks/actions';
import { openPrompt } from 'redux-core/prompt/actions';
import enhancer from './enhancer';
import FileDropZone from './FileDropZone';
import ErrorOnSpreadsheet from './ErrorOnSpreadsheet';
import ReadyToImport from './ReadyToImport';
import { importingPrompt } from './ImportingPrompt';

const rootT = 'drawers.importCommercialTracksDrawer';

const FormatedLiTagComponent = ({ children }) => (
  <li>
    <Text variant="formLabel" children={children} />
  </li>
);

const ImportCommercialTracksDrawer = ({ onClose, currentUserId, productionId }) => {
  const t = useRootTranslation(rootT);
  const dispatch = useDispatch();
  const [readyImport, setReadyImport] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  // Escape/backdrop dismiss (handled by withBaseDrawer/BaseDrawer) unmounts this component
  // directly, bypassing the disabled Cancel/Import buttons — it isn't just visually hidden.
  // handleImport uses this to avoid setState after unmount once its async flow resumes.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const downloadTemplate = async () => {
    await getCommercialTemplateFile();
  };

  const handleFile = async (file) => {
    setIsValidating(true);
    const result = await validateCommercialImportFile({ file });
    setIsValidating(false);
    if (result.ok) {
      setReadyImport(true);
      setErrors([]);
      setSelectedFile(file);
    } else {
      setReadyImport(false);
      setErrors(result.errors);
      setSelectedFile(null);
    }
  };

  const handleRejected = (rejectedErrors) => {
    setReadyImport(false);
    setErrors(rejectedErrors);
    setSelectedFile(null);
  };

  const handleTryAgain = () => {
    setReadyImport(null);
    setErrors([]);
    setSelectedFile(null);
  };

  const handleImport = async () => {
    // Defense-in-depth: the Import button is already disabled while isImporting is true, but
    // that alone doesn't stop a second handleImport call queued before the first re-render
    // (e.g. two synchronous clicks) — this guard makes the no-double-start guarantee explicit.
    if (isImporting) return;
    setIsImporting(true);

    let s3FileKey;
    try {
      s3FileKey = await uploadCommercialImportFile(selectedFile);
    } catch (uploadError) {
      // If the drawer was dismissed (Escape/backdrop) while the upload was in flight, there's
      // nowhere left to render this error — skip the state updates entirely.
      if (!mountedRef.current) return;
      setIsImporting(false);
      setReadyImport(false);
      setErrors([{ code: 'IMPORT_START_FAILED' }]);
      return;
    }

    try {
      await startCommercialImportCall({ productionId, userId: currentUserId, s3FileKey });
    } catch (startError) {
      // If the drawer was dismissed (Escape/backdrop) while the start call was in flight, there's
      // nowhere left to render this error — skip the state updates entirely.
      if (!mountedRef.current) return;
      setIsImporting(false);
      setReadyImport(false);
      setErrors([{ code: 'IMPORT_START_FAILED' }]);
      return;
    }

    // The import HAS genuinely started server-side at this point. openPrompt dispatches into
    // GLOBAL dialog state (redux-core/prompt) that lives outside this drawer and outlives its
    // unmount, so it still fires even if the drawer was dismissed mid-flight — the user must
    // learn the import is running regardless. onClose() is skipped once unmounted, though: the
    // drawer is already gone, so there's nothing left to close.
    dispatch(openPrompt(importingPrompt(t)));
    if (!mountedRef.current) return;
    setIsImporting(false);
    onClose();
  };

  return (
    <>
      <DrawerHeader bold hideBackButton onClose={onClose} title={t('title')}>
        <Button
          alwaysEnabled
          children={t('cancel')}
          disabled={isImporting}
          onClick={onClose}
          size="xs"
          synchronous
          testId="import-commercial-tracks-cancel"
          variant="destructive"
        />
        <Button
          children={t('import')}
          disabled={readyImport !== true || isImporting}
          onClick={handleImport}
          size="sm"
          testId="import-commercial-tracks-import"
          type="submit"
          variant="primary"
        />
      </DrawerHeader>
      <Grid container spacing={2}>
        <Divider sx={{ my: 2 }} />
        <Grid item xs={9}>
          <Text children={t('intro')} />
        </Grid>
        <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            endIcon={<FileDownloadIcon />}
            size="sm"
            variant="text"
            children={t('downloadTemplate')}
            onClick={downloadTemplate}
            type="button"
          />
        </Grid>
        <Grid container direction="column">
          <Text children={t('steps.title')} bold />
          <Grid item>
            <Trans i18nKey={`${rootT}.steps.step1`}>
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
            </Trans>
          </Grid>
          <Grid item>
            <Trans i18nKey={`${rootT}.steps.step2`}>
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
              <ul style={{ marginTop: 0, marginBottom: 8 }}>
                <FormatedLiTagComponent />
                <FormatedLiTagComponent />
                <FormatedLiTagComponent />
                <FormatedLiTagComponent />
                <FormatedLiTagComponent />
                <FormatedLiTagComponent />
              </ul>
            </Trans>
          </Grid>
          <Grid item>
            <Trans i18nKey={`${rootT}.steps.step3`}>
              <Text variant="formLabel" bold />
              <Text variant="formLabel" />
            </Trans>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {isValidating && <Loading />}
          {!isValidating && readyImport === null && <FileDropZone onFile={handleFile} onRejected={handleRejected} />}
          {!isValidating && readyImport === false && <ErrorOnSpreadsheet errors={errors} onTryAgain={handleTryAgain} />}
          {!isValidating && readyImport === true && <ReadyToImport />}
        </Grid>
      </Grid>
    </>
  );
};

export default enhancer(ImportCommercialTracksDrawer);
